// File: src/components/AdminServices.js
// COMPLETE FIXED VERSION - Debug delete service function

import React, { useState } from 'react';
import { Plus, Edit2, Trash2, DollarSign, Wrench } from 'lucide-react';

const AdminServices = ({
  services,
  coupons,
  setCoupons,
  createService,
  updateService,
  deleteService,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  bundles = [],
  createBundle,
  updateBundle,
  deleteBundle
}) => {
  const [newService, setNewService] = useState({ name: '', price: '', description: '', duration: '45' });
  const [editingService, setEditingService] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    type: 'percentage',
    value: '',
    expiryDate: '',
    description: '',
    service_id: ''
  });
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [newBundle, setNewBundle] = useState({ name: '', bundle_price: '', description: '', service_ids: [] });
  const [editingBundle, setEditingBundle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enhanced delete service function
  const handleDeleteService = async (serviceId, serviceName) => {
    if (!serviceId) {
      return;
    }

    if (!deleteService || typeof deleteService !== 'function') {
      return;
    }

    const confirmMessage = `Are you sure you want to delete "${serviceName || 'this service'}"?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await deleteService(serviceId);
    } catch (error) {
      // Error deleting service
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced service addition with better error handling
  const addService = async () => {
    if (!newService.name || (newService.price === '' || newService.price === null || newService.price === undefined)) {
      return;
    }

    if (isNaN(Number(newService.price)) || Number(newService.price) < 0) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const serviceData = {
        name: newService.name.trim(),
        price: parseFloat(newService.price),
        description: newService.description.trim() || '',
        duration: parseInt(newService.duration) || 45
      };

      console.log('Creating service:', serviceData);
      const result = await createService(serviceData);
      console.log('Create service result:', result);

      if (result && result.success) {
        setNewService({ name: '', price: '', description: '', duration: '45' });
        alert('Service created successfully!');
      } else {
        const errorMessage = result?.error || 'Unknown error occurred';
        alert(`Failed to create service: ${errorMessage}`);
      }
    } catch (error) {
      console.error('💥 Exception during service creation:', error);
      alert(`Error creating service: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateService = async () => {
    if (!editingService.name || (editingService.price === '' || editingService.price === null || editingService.price === undefined)) {
      alert('Please provide service name and price');
      return;
    }

    if (isNaN(Number(editingService.price)) || Number(editingService.price) < 0) {
      alert('Please provide a valid price');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const serviceData = {
        id: editingService.id,
        name: editingService.name.trim(),
        price: parseFloat(editingService.price),
        description: editingService.description.trim() || '',
        duration: parseInt(editingService.duration) || 45
      };

      console.log('Updating service:', serviceData);
      const result = await updateService(serviceData);
      console.log('Update service result:', result);
      
      if (result && result.success) {
        setEditingService(null);
        alert('Service updated successfully!');
      } else {
        const errorMessage = result?.error || 'Unknown error occurred';
        alert(`Failed to update service: ${errorMessage}`);
      }
    } catch (error) {
      console.error('💥 Exception during service update:', error);
      alert(`Error updating service: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Coupon management functions
  const addCoupon = async () => {
    if (!newCoupon.code || !newCoupon.value || !newCoupon.expiryDate) {
      alert('Please provide coupon code, value, and expiry date');
      return;
    }
    
    if (coupons.some(c => c.code.toLowerCase() === newCoupon.code.toLowerCase())) {
      alert('Coupon code already exists');
      return;
    }

    if (newCoupon.type === 'percentage' && parseFloat(newCoupon.value) > 100) {
      alert('Percentage cannot exceed 100%');
      return;
    }

    if (parseFloat(newCoupon.value) <= 0) {
      alert('Please provide a valid coupon value');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const couponData = {
        code: newCoupon.code.trim(),
        type: newCoupon.type,
        value: parseFloat(newCoupon.value),
        expiryDate: newCoupon.expiryDate,
        description: newCoupon.description.trim() || '',
        active: true,
        service_id: newCoupon.service_id || null
      };
      
      const result = await createCoupon(couponData);
      
      if (result && result.success) {
        setNewCoupon({ code: '', type: 'percentage', value: '', expiryDate: '', description: '', service_id: '' });
        alert('Coupon created successfully!');
      } else {
        alert('Failed to create coupon: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Exception during coupon creation:', error);
      alert(`Error creating coupon: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateCouponHandler = async () => {
    const expiryDate = editingCoupon.expiryDate || editingCoupon.expiry_date;

    if (!editingCoupon.code || !editingCoupon.value || !expiryDate) {
      alert('Please provide coupon code, value, and expiry date');
      return;
    }

    if (editingCoupon.type === 'percentage' && parseFloat(editingCoupon.value) > 100) {
      alert('Percentage cannot exceed 100%');
      return;
    }

    if (parseFloat(editingCoupon.value) <= 0) {
      alert('Please provide a valid coupon value');
      return;
    }

    setIsSubmitting(true);

    try {
      const couponData = {
        id: editingCoupon.id,
        code: editingCoupon.code.trim(),
        type: editingCoupon.type,
        value: parseFloat(editingCoupon.value),
        expiryDate: expiryDate,  // Use the resolved date
        description: (editingCoupon.description || '').trim(),
        active: editingCoupon.active,
        service_id: editingCoupon.service_id || null
      };

      console.log('📤 Sending coupon update:', couponData);

      const result = await updateCoupon(couponData);

      if (result && result.success) {
        setEditingCoupon(null);
        alert('Coupon updated successfully!');
      } else {
        alert('Failed to update coupon: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Exception during coupon update:', error);
      alert(`Error updating coupon: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleCouponStatus = async (couponId) => {
    const coupon = coupons.find(c => c.id === couponId);
    if (!coupon) return;

    setIsSubmitting(true);
    
    try {
      const result = await updateCoupon({
        id: coupon.id,
        active: !coupon.active
      });
      
      if (!result || !result.success) {
        alert('Failed to update coupon status: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Exception during coupon status toggle:', error);
      alert(`Error updating coupon status: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteCouponHandler = async (couponId, couponCode) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${couponCode}"?`)) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await deleteCoupon(couponId);
      
      if (result && result.success) {
        alert('Coupon deleted successfully!');
      } else {
        alert('Failed to delete coupon: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Exception during coupon deletion:', error);
      alert(`Error deleting coupon: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function to format date string as YYYY-MM-DD without timezone conversion
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If it's already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Otherwise parse and format
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to display date in local format without timezone issues
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    // Parse as local date by splitting the string
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  };

  // Helper function to compare dates without timezone issues
  const isDateExpired = (dateString) => {
    if (!dateString) return true;
    const [year, month, day] = dateString.split('-').map(Number);
    const expiryDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiryDate < today;
  };

  // Bundle management functions
  const handleBundleServiceToggle = (serviceId, isEditing = false) => {
    const sid = serviceId.toString();
    if (isEditing) {
      setEditingBundle(prev => {
        const current = (prev.service_ids || []).map(String);
        const updated = current.includes(sid) ? current.filter(id => id !== sid) : [...current, sid];
        return { ...prev, service_ids: updated };
      });
    } else {
      setNewBundle(prev => {
        const current = (prev.service_ids || []).map(String);
        const updated = current.includes(sid) ? current.filter(id => id !== sid) : [...current, sid];
        return { ...prev, service_ids: updated };
      });
    }
  };

  const addBundle = async () => {
    if (!newBundle.name || !newBundle.bundle_price) {
      alert('Please provide bundle name and price');
      return;
    }
    if (newBundle.service_ids.length < 2) {
      alert('Please select at least 2 services for a bundle');
      return;
    }
    if (parseFloat(newBundle.bundle_price) <= 0) {
      alert('Please provide a valid bundle price');
      return;
    }

    setIsSubmitting(true);
    try {
      const bundleData = {
        name: newBundle.name.trim(),
        bundle_price: parseFloat(newBundle.bundle_price),
        description: newBundle.description.trim() || '',
        service_ids: newBundle.service_ids.map(Number),
        active: true
      };

      const result = await createBundle(bundleData);
      if (result && result.success) {
        setNewBundle({ name: '', bundle_price: '', description: '', service_ids: [] });
        alert('Bundle created successfully!');
      } else {
        alert('Failed to create bundle: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      alert(`Error creating bundle: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBundle = async () => {
    if (!editingBundle.name || !editingBundle.bundle_price) {
      alert('Please provide bundle name and price');
      return;
    }
    if ((editingBundle.service_ids || []).length < 2) {
      alert('Please select at least 2 services');
      return;
    }

    setIsSubmitting(true);
    try {
      const bundleData = {
        id: editingBundle.id,
        name: editingBundle.name.trim(),
        bundle_price: parseFloat(editingBundle.bundle_price),
        description: (editingBundle.description || '').trim(),
        service_ids: editingBundle.service_ids.map(Number),
        active: editingBundle.active
      };

      const result = await updateBundle(bundleData);
      if (result && result.success) {
        setEditingBundle(null);
        alert('Bundle updated successfully!');
      } else {
        alert('Failed to update bundle: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      alert(`Error updating bundle: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBundleHandler = async (bundleId, bundleName) => {
    if (!window.confirm(`Are you sure you want to delete bundle "${bundleName}"?`)) return;

    setIsSubmitting(true);
    try {
      const result = await deleteBundle(bundleId);
      if (result && result.success) {
        alert('Bundle deleted successfully!');
      } else {
        alert('Failed to delete bundle: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      alert(`Error deleting bundle: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBundleIndividualTotal = (serviceIds) => {
    return (serviceIds || []).reduce((total, sid) => {
      const svc = services.find(s => s.id.toString() === sid.toString());
      return total + (svc ? parseFloat(svc.price) : 0);
    }, 0);
  };

  const calculateRevenue = () => {
    return services.reduce((total, service) => total + service.price, 0);
  };

  return (
    <div className="space-y-6">
      {/* Debug Information Panel */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-yellow-800 mb-2">🐛 Debug Info</h3>
        <div className="text-xs text-yellow-700 space-y-1">
          <p><strong>Services count:</strong> {services?.length || 0}</p>
          <p><strong>deleteService function:</strong> {typeof deleteService}</p>
          <p><strong>Is submitting:</strong> {isSubmitting ? 'Yes' : 'No'}</p>
          <p><strong>Services array:</strong> {services ? 'Available' : 'Not available'}</p>
          {services?.length > 0 && (
            <p><strong>Sample service ID:</strong> {services[0]?.id} (type: {typeof services[0]?.id})</p>
          )}
        </div>
      </div>

      {/* Services Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Services</p>
              <p className="text-2xl font-bold text-gray-900">{services.length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Wrench className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Service Price</p>
              <p className="text-2xl font-bold text-gray-900">
                ${services.length > 0 ? (calculateRevenue() / services.length).toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Coupons</p>
              <p className="text-2xl font-bold text-gray-900">{coupons.filter(c => c.active).length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <span className="text-purple-600 font-bold text-lg">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Services Management */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">Manage Services</h3>
            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              Database Synced
            </div>
          </div>
          
          {/* Add New Service */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-700 mb-3">Add New Service</h4>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Service name"
                value={newService.name}
                onChange={(e) => setNewService(prev => ({...prev, name: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                disabled={isSubmitting}
              />
              <input
                type="number"
                placeholder="Price"
                step="0.01"
                min="0"
                value={newService.price}
                onChange={(e) => setNewService(prev => ({...prev, price: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                disabled={isSubmitting}
              />
              <div>
                <label className="block text-xs text-gray-600 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  placeholder="45"
                  min="15"
                  step="15"
                  value={newService.duration}
                  onChange={(e) => setNewService(prev => ({...prev, duration: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  disabled={isSubmitting}
                />
              </div>
              <textarea
                placeholder="Description (optional)"
                value={newService.description}
                onChange={(e) => setNewService(prev => ({...prev, description: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="2"
                disabled={isSubmitting}
              />
              <button
                onClick={addService}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                disabled={isSubmitting}
              >
                <Plus className="w-4 h-4" />
                {isSubmitting ? 'Adding...' : 'Add Service'}
              </button>
            </div>
          </div>

          {/* Current Services */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">Current Services ({services.length})</h4>
            {services.map(service => (
              <div key={service.id} className="border border-gray-200 rounded-lg p-3">
                {editingService && editingService.id === service.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingService.name}
                      onChange={(e) => setEditingService(prev => ({...prev, name: e.target.value}))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      disabled={isSubmitting}
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingService.price}
                      onChange={(e) => setEditingService(prev => ({...prev, price: e.target.value}))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      disabled={isSubmitting}
                    />
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Duration (minutes)</label>
                      <input
                        type="number"
                        min="15"
                        step="15"
                        value={editingService.duration || 45}
                        onChange={(e) => setEditingService(prev => ({...prev, duration: e.target.value}))}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        disabled={isSubmitting}
                      />
                    </div>
                    <textarea
                      value={editingService.description}
                      onChange={(e) => setEditingService(prev => ({...prev, description: e.target.value}))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      rows="2"
                      disabled={isSubmitting}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateService}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingService(null)}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <h5 className="font-medium text-gray-800">{service.name}</h5>
                      <p className="text-lg font-bold text-green-600">${service.price}</p>
                      <p className="text-xs text-gray-500">{service.duration || 45} min</p>
                      {service.description && (
                        <p className="text-xs text-gray-500 mt-1">{service.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">ID: {service.id}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingService(service)}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={isSubmitting}
                        title="Edit service"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {/* FIXED: Enhanced delete button with better logging */}
                      <button
                        onClick={() => handleDeleteService(service.id, service.name)}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        disabled={isSubmitting}
                        title={`Delete ${service.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Coupon Management */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">Manage Coupons</h3>
            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              Database Synced
            </div>
          </div>
          
          {/* Add New Coupon */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-700 mb-3">Add New Coupon</h4>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Coupon code (e.g., SAVE20)"
                value={newCoupon.code}
                onChange={(e) => setNewCoupon(prev => ({...prev, code: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                disabled={isSubmitting}
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={newCoupon.type}
                  onChange={(e) => setNewCoupon(prev => ({...prev, type: e.target.value}))}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  disabled={isSubmitting}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
                <input
                  type="number"
                  placeholder={newCoupon.type === 'percentage' ? "20" : "10"}
                  step="0.01"
                  min="0"
                  max={newCoupon.type === 'percentage' ? "100" : undefined}
                  value={newCoupon.value}
                  onChange={(e) => setNewCoupon(prev => ({...prev, value: e.target.value}))}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={newCoupon.expiryDate}
                  onChange={(e) => setNewCoupon(prev => ({...prev, expiryDate: e.target.value}))}
                  min={getMinDate()}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  disabled={isSubmitting}
                />
              </div>
              <textarea
                placeholder="Description (optional)"
                value={newCoupon.description}
                onChange={(e) => setNewCoupon(prev => ({...prev, description: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="2"
                disabled={isSubmitting}
              />
              <div>
                <label className="block text-xs text-gray-600 mb-1">Applies to Service</label>
                <select
                  value={newCoupon.service_id}
                  onChange={(e) => setNewCoupon(prev => ({...prev, service_id: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  disabled={isSubmitting}
                >
                  <option value="">All Services</option>
                  {services.filter(s => s.active).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={addCoupon}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                disabled={isSubmitting}
              >
                <Plus className="w-4 h-4" />
                {isSubmitting ? 'Adding...' : 'Add Coupon'}
              </button>
            </div>
          </div>

          {/* Current Coupons */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            <h4 className="font-semibold text-gray-700">Current Coupons ({coupons.length})</h4>
            {coupons.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No coupons created</p>
            ) : (
              coupons.map(coupon => (
                <div key={coupon.id} className="border border-gray-200 rounded-lg p-3">
                  {editingCoupon && editingCoupon.id === coupon.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingCoupon.code}
                        onChange={(e) => setEditingCoupon(prev => ({...prev, code: e.target.value}))}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        disabled={isSubmitting}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={editingCoupon.type}
                          onChange={(e) => setEditingCoupon(prev => ({...prev, type: e.target.value}))}
                          className="border border-gray-300 rounded px-2 py-1"
                          disabled={isSubmitting}
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                        </select>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={editingCoupon.type === 'percentage' ? "100" : undefined}
                          value={editingCoupon.value}
                          onChange={(e) => setEditingCoupon(prev => ({...prev, value: e.target.value}))}
                          className="border border-gray-300 rounded px-2 py-1"
                          disabled={isSubmitting}
                        />
                      </div>
                      <input
                        type="date"
                        value={formatDateForInput(editingCoupon.expiryDate || editingCoupon.expiry_date)}
                        onChange={(e) => setEditingCoupon(prev => ({...prev, expiryDate: e.target.value}))}
                        min={getMinDate()}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        disabled={isSubmitting}
                      />
                      <textarea
                        value={editingCoupon.description || ''}
                        onChange={(e) => setEditingCoupon(prev => ({...prev, description: e.target.value}))}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        rows="2"
                        disabled={isSubmitting}
                      />
                      <select
                        value={editingCoupon.service_id || ''}
                        onChange={(e) => setEditingCoupon(prev => ({...prev, service_id: e.target.value}))}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        disabled={isSubmitting}
                      >
                        <option value="">All Services</option>
                        {services.filter(s => s.active).map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={updateCouponHandler}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingCoupon(null)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h5 className="font-medium text-gray-800">{coupon.code}</h5>
                          <span className={`text-xs px-2 py-1 rounded ${
                            coupon.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {coupon.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            !isDateExpired(coupon.expiry_date || coupon.expiryDate) ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {!isDateExpired(coupon.expiry_date || coupon.expiryDate) ? 'Valid' : 'Expired'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {coupon.type === 'percentage' ? `${coupon.value}% off` : `$${coupon.value} off`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Expires: {formatDateDisplay(coupon.expiry_date || coupon.expiryDate)}
                        </p>
                        {coupon.description && (
                          <p className="text-xs text-gray-500 mt-1">{coupon.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Applies to: {coupon.service_id
                            ? (services.find(s => s.id === coupon.service_id)?.name || 'Unknown Service')
                            : 'All Services'}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => toggleCouponStatus(coupon.id)}
                          className={`text-sm px-2 py-1 rounded disabled:opacity-50 ${
                            coupon.active ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                          disabled={isSubmitting}
                        >
                          {coupon.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => setEditingCoupon(coupon)}
                          className="text-blue-600 hover:text-blue-800"
                          disabled={isSubmitting}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteCouponHandler(coupon.id, coupon.code)}
                          className="text-red-600 hover:text-red-800"
                          disabled={isSubmitting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Service Bundles Management */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Manage Service Bundles</h3>
          <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
            Auto-applies at checkout
          </div>
        </div>

        {/* Add New Bundle */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-700 mb-3">Create New Bundle</h4>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Bundle name (e.g., Spring Special)"
              value={newBundle.name}
              onChange={(e) => setNewBundle(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={isSubmitting}
            />
            <input
              type="number"
              placeholder="Bundle price"
              step="0.01"
              min="0"
              value={newBundle.bundle_price}
              onChange={(e) => setNewBundle(prev => ({ ...prev, bundle_price: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={isSubmitting}
            />
            <textarea
              placeholder="Description (optional)"
              value={newBundle.description}
              onChange={(e) => setNewBundle(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows="2"
              disabled={isSubmitting}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Services (min 2):</label>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                {services.map(service => (
                  <label key={service.id} className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBundle.service_ids.map(String).includes(service.id.toString())}
                      onChange={() => handleBundleServiceToggle(service.id, false)}
                      disabled={isSubmitting}
                      className="text-green-600 rounded"
                    />
                    <span className="text-sm">{service.name} - ${parseFloat(service.price).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>

            {newBundle.service_ids.length >= 2 && newBundle.bundle_price && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                <p><strong>Individual Total:</strong> ${getBundleIndividualTotal(newBundle.service_ids).toFixed(2)}</p>
                <p><strong>Bundle Price:</strong> ${parseFloat(newBundle.bundle_price).toFixed(2)}</p>
                <p className="text-green-700 font-semibold">
                  Savings: ${(getBundleIndividualTotal(newBundle.service_ids) - parseFloat(newBundle.bundle_price)).toFixed(2)}
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Create Bundle Button - Outside the gray form box for visibility */}
        <div className="mb-6">
          <button
            onClick={addBundle}
            className={`w-full px-6 py-4 rounded-lg flex items-center gap-2 font-bold text-lg justify-center ${
              isSubmitting || newBundle.service_ids.length < 2 || !newBundle.name || !newBundle.bundle_price
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700 text-white cursor-pointer'
            }`}
            disabled={isSubmitting || newBundle.service_ids.length < 2 || !newBundle.name || !newBundle.bundle_price}
          >
            <Plus className="w-6 h-6" />
            {isSubmitting ? 'Creating Bundle...' : 'Create Bundle'}
          </button>
          {newBundle.service_ids.length < 2 && (
            <p className="text-sm text-red-500 mt-2 text-center font-medium">
              Select at least 2 services above to create a bundle
            </p>
          )}
        </div>

        {/* Current Bundles */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-700">Current Bundles ({bundles.filter(b => b.active).length} active)</h4>
          {bundles.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No bundles created yet</p>
          ) : (
            bundles.map(bundle => (
              <div key={bundle.id} className={`border rounded-lg p-3 ${bundle.active ? 'border-gray-200' : 'border-red-200 bg-red-50'}`}>
                {editingBundle && editingBundle.id === bundle.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingBundle.name}
                      onChange={(e) => setEditingBundle(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      disabled={isSubmitting}
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingBundle.bundle_price}
                      onChange={(e) => setEditingBundle(prev => ({ ...prev, bundle_price: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      disabled={isSubmitting}
                    />
                    <textarea
                      value={editingBundle.description || ''}
                      onChange={(e) => setEditingBundle(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      rows="2"
                      disabled={isSubmitting}
                    />
                    <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                      {services.map(service => (
                        <label key={service.id} className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(editingBundle.service_ids || []).map(String).includes(service.id.toString())}
                            onChange={() => handleBundleServiceToggle(service.id, true)}
                            disabled={isSubmitting}
                            className="text-green-600 rounded"
                          />
                          <span className="text-sm">{service.name} - ${parseFloat(service.price).toFixed(2)}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateBundle}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingBundle(null)}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-medium text-gray-800">{bundle.name}</h5>
                        <span className={`text-xs px-2 py-1 rounded ${bundle.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {bundle.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-lg font-bold text-green-600">${parseFloat(bundle.bundle_price).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">
                        Services: {(bundle.services || []).map(s => s.name).join(', ')}
                      </p>
                      <p className="text-xs text-green-600 font-medium">
                        Save ${bundle.savings?.toFixed(2)} (Individual: ${bundle.individual_total?.toFixed(2)})
                      </p>
                      {bundle.description && (
                        <p className="text-xs text-gray-500 mt-1">{bundle.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => setEditingBundle({ ...bundle })}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={isSubmitting}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBundleHandler(bundle.id, bundle.name)}
                        className="text-red-600 hover:text-red-800"
                        disabled={isSubmitting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminServices;